import { Alert, Box, CircularProgress } from '@munifocus/ui';
import { useTranslation } from 'react-i18next';
import { Navigate, useParams } from 'react-router-dom';
import type { EscalationStageData } from '@munifocus/contracts-gov';
import { AppShell } from '../components/AppShell';
import { useFile } from '../lib/files-api';
import { escalationStepFromSlug } from '../lib/escalation-display';
import { Step5Notice } from './escalation/Step5Notice';
import { Step6Committee } from './escalation/Step6Committee';
import { Step7Council } from './escalation/Step7Council';
import { Step8Cleanup } from './escalation/Step8Cleanup';
import { Step9Proceedings } from './escalation/Step9Proceedings';
import { FileLoadError } from '../components/FileLoadError';

/**
 * Entry point for the Type 3 sub-flow steps at `/files/:id/escalation/:step`.
 * Guards: the file must have reached the escalation stage with an authorized
 * escalation type (ticket E1) before any step screen renders.
 */
export function EscalationStep() {
  const { id = '', step = '' } = useParams();
  const { t } = useTranslation();
  const { data: file, isLoading, error } = useFile(id);

  if (isLoading || !file) {
    return (
      <AppShell>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          {isLoading ? <CircularProgress /> : <FileLoadError error={error} />}
        </Box>
      </AppShell>
    );
  }

  const escalation = (file.stageData?.escalation ?? {}) as EscalationStageData;
  // Steps 7 and 8 can close the file. That flips `stage` to `closed`, and the
  // refreshed file re-renders this guard while the step's own success handler is
  // still navigating to the detail page — so send a closed file there too rather
  // than bouncing the officer back to the type selector it already cleared.
  if (file.stage === 'closed') {
    return <Navigate to={`/files/${id}`} replace />;
  }
  // Not yet authorized — send the officer to the escalation type selector (E1).
  if (file.stage !== 'escalation' || !escalation.escalationType) {
    return <Navigate to={`/files/${id}/stage/escalation`} replace />;
  }

  const stepId = escalationStepFromSlug(step);
  if (stepId === 'step_5') return <Step5Notice file={file} />;
  if (stepId === 'step_6') return <Step6Committee file={file} />;
  if (stepId === 'step_7') return <Step7Council file={file} />;
  if (stepId === 'step_8') return <Step8Cleanup file={file} />;
  if (stepId === 'step_9') return <Step9Proceedings file={file} />;

  // Unknown step slug — fall back to the file detail page.
  return <Navigate to={`/files/${id}`} replace />;
}
